import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';
import { useLocation } from '../../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../../contexts/BusinessTypeContext';
import { useMutation, useMasterData } from '../../../hooks/useApi';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../../utils/api';
import { GoatMaster, TreatmentType } from '../../../types/api';

interface GoatHealthFormData {
  tag: string;
  health_date: string;
  treatment_type: TreatmentType;
  medicine?: string;
  dose?: string;
  notes?: string;
}

const healthSchema = yup.object({
  tag: yup.string().required('Goat tag is required'),
  health_date: yup.string().required('Health date is required'),
  treatment_type: yup.string().oneOf(['vaccination', 'deworming', 'treatment', 'other']).required('Treatment type is required'),
  medicine: yup.string().optional(),
  dose: yup.string().optional(),
  notes: yup.string().optional(),
});

const GoatHealthForm: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const [error, setError] = useState('');
  const [goats, setGoats] = useState<GoatMaster[]>([]);
  const [loadingGoats, setLoadingGoats] = useState(true);
  
  // Load medicines from master data
  const { data: medicinesData, loading: loadingMedicines } = useMasterData('goat_medicine_types', selectedLocation?.id || null);
  const medicines = medicinesData || [];

  const { mutate: createHealthLog, loading: creating } = useMutation<any, GoatHealthFormData>();

  const form = useForm<GoatHealthFormData>({
    resolver: yupResolver(healthSchema) as any,
    defaultValues: {
      tag: '',
      health_date: new Date().toISOString().split('T')[0],
      treatment_type: 'treatment',
      medicine: '',
      dose: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (businessType === 'goat' && selectedLocation?.id) {
      loadGoats();
    }
  }, [selectedLocation?.id, businessType]);

  const loadGoats = async () => {
    try {
      setLoadingGoats(true);
      const response = await apiClient.get(`/goat/master?location_id=${selectedLocation?.id}`);
      setGoats((response.data || []).filter((g: GoatMaster) => g.status === 'active'));
    } catch (err) {
      console.error('Failed to load goats:', err);
    } finally {
      setLoadingGoats(false);
    }
  };

  const handleSubmit = async (data: GoatHealthFormData) => {
    try {
      setError('');
      const healthData = {
        ...data,
        location_id: selectedLocation?.id,
      };
      await createHealthLog('/goat/health-logs', healthData);
      navigate('/goat/health-logs');
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to create health record'));
    }
  };

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This form is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/goat/health-logs" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">💊 Record Health Treatment</h1>
          <p className="text-gray-600">{selectedLocation.name}</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Form */}
      <div className="card p-6">
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Goat Tag *</label>
              {loadingGoats ? (
                <div className="form-input bg-gray-50 flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-sm text-gray-500">Loading goats...</span>
                </div>
              ) : (
                <select {...form.register('tag')} className="form-input">
                  <option value="">Select Goat</option>
                  {goats.map((goat) => (
                    <option key={goat.id} value={goat.tag_no}>
                      {goat.tag_no} ({goat.category}) {goat.breed ? `- ${goat.breed}` : ''}
                    </option>
                  ))}
                </select>
              )}
              {form.formState.errors.tag && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.tag.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Health Date *</label>
              <input
                type="date"
                {...form.register('health_date')}
                className="form-input"
              />
              {form.formState.errors.health_date && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.health_date.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Treatment Type *</label>
              <select {...form.register('treatment_type')} className="form-input">
                <option value="vaccination">Vaccination</option>
                <option value="deworming">Deworming</option>
                <option value="treatment">Treatment</option>
                <option value="other">Other</option>
              </select>
              {form.formState.errors.treatment_type && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.treatment_type.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Medicine</label>
              {loadingMedicines ? (
                <div className="form-input bg-gray-50 flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-sm text-gray-500">Loading medicines...</span>
                </div>
              ) : (
                <select {...form.register('medicine')} className="form-input">
                  <option value="">Select Medicine</option>
                  {medicines.map((medicine: any) => (
                    <option key={medicine.id} value={medicine.name}>
                      {medicine.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="form-label">Dose</label>
              <input
                type="text"
                {...form.register('dose')}
                className="form-input"
                placeholder="e.g., 5ml, 1 tablet"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="form-label">Notes</label>
              <textarea
                {...form.register('notes')}
                className="form-input"
                rows={3}
                placeholder="Additional notes about the treatment"
              />
            </div>
          </div>

          <div className="flex space-x-4 pt-4">
            <button
              type="submit"
              disabled={creating}
              className="flex-1 btn-touch bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
            >
              {creating ? <LoadingSpinner size="sm" /> : 'Record Treatment'}
            </button>
            <Link
              to="/goat/health-logs"
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GoatHealthForm;
